import { useEffect } from 'react';
import { Autocomplete } from 'ovComponents/2-component';
import { type TFunction, useTranslation } from 'react-i18next';
import { translateBackend } from 'assets/i18n/config';
import { Box } from '../..';
import { useCountrySpecificBackendIdDocumentTypes } from '../../../interfaces/IdVerification';

interface VerificationMethod {
  key: string;
  label: {
    en: string;
    fr: string;
  };
}

type VerificationMethodKey =
  | 'allowVerificationMethodCreditCheck'
  | 'allowVerificationMethodDigitalDocumentCheck'
  | 'allowVerificationMethodDual'
  | 'allowVerificationMethodInPerson';

interface SubStepOption {
  key: string;
  label: {
    en: string;
    fr: string;
  };
  optionType: string;
  options: {
    allowMultiple: boolean;
    items: VerificationMethod[];
  };
  showIf: {
    [key in VerificationMethodKey]?: boolean;
  };
  default: string | string[];
  value: string | string[];
}

interface Props {
  option: SubStepOption;
  options: SubStepOption[];
  setOptions: (options: SubStepOption[]) => void;
  i: number;
}
interface OptionItem {
  key: string;
  label: string;
}

const methodDocumentKey = ['digitalDocumentTypes', 'creditCheckDocumentTypes', 'dualVerificationTypes', 'inPersonVerificationTypes'] as const;
type MethodDocumentKey = (typeof methodDocumentKey)[number];

interface VerificationMethod {
  documentKey: MethodDocumentKey;
  showIfKey: string;
  label: {
    en: string;
    fr: string;
  };
}

export const AutocompleteFieldIdVerification: React.FC<Props> = ({
  option, options, setOptions, i,
}) => {
  const { t } = useTranslation('client');

  const { idDocumentTypes } = useCountrySpecificBackendIdDocumentTypes();
  const verificationMethods = [
    {
      key: 'creditCheckDocumentTypes',
      showIfKey: 'allowVerificationMethodCreditCheck' as const,
      label: t('documentTypesLabels.creditCheckDocumentTypes'),
    },
    {
      key: 'digitalDocumentTypes',
      showIfKey: 'allowVerificationMethodDigitalDocumentCheck' as const,
      label: t('documentTypesLabels.digitalDocumentTypes'),
    },
    {
      key: 'dualVerificationTypes',
      showIfKey: 'allowVerificationMethodDual' as const,
      label: t('documentTypesLabels.dualVerificationTypes'),
    },
    {
      key: 'inPersonVerificationTypes',
      showIfKey: 'allowVerificationMethodInPerson' as const,
      label: t('documentTypesLabels.inPersonVerificationTypes'),
    },
  ] as const;

  type IdDocumentOption = { key: string; label: string };

  function getDefaultDocumentOptions(): OptionItem[] {
    if (option.options?.items) {
      return option.options.items.map((item: any) => ({
        key: item.key || item,
        label: typeof item === 'string' ? t(`documentTypes.${item}`) : translateBackend(item.label),
      }));
    }
    return [];
  }

  function getCombinedIdDocumentOptions(
    defaultItems: OptionItem[],
    localizedTypes: string[],
    translation: TFunction,
  ): IdDocumentOption[] {
    const idDocumentsBasedOnLocalization = (localizedTypes || [])
      .map((type: string) => ({
        key: type,
        label: translation(`documentTypes.${type}`),
      }));

    const uniqueIdDocumentTypes = idDocumentsBasedOnLocalization.filter(
      (docType) => !defaultItems.some((existing) => existing.key === docType.key),
    );

    return [...defaultItems, ...uniqueIdDocumentTypes]
      .sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()));
  }

  const idDocumentsDefault = getDefaultDocumentOptions();

  const allOptions = getCombinedIdDocumentOptions(
    idDocumentsDefault,
    idDocumentTypes,
    t,
  );

  useEffect(() => {
    const isDualVerification = option.key === 'dualMethodDocumentTypes'
                              && option.showIf?.allowVerificationMethodDual;

    if (!isDualVerification || allOptions.length === 0) return;

    const currentKeys = Array.isArray(option.value) ? option.value : (option.value ? [option.value] : []);
    const allKeys = allOptions.map((item) => item.key);
    const baseKeys = idDocumentsDefault.map((item) => item.key);

    const isIncompleteInitialState = currentKeys.length === baseKeys.length
      && currentKeys.every((key) => baseKeys.includes(key))
      && baseKeys.every((key) => currentKeys.includes(key));

    if (isIncompleteInitialState && allKeys.length > baseKeys.length) {
      const newOptions = [...options];
      newOptions[i] = {
        ...newOptions[i],
        value: allKeys.sort(),
      };
      setOptions(newOptions);
    }
  }, [allOptions.length, allOptions, i, idDocumentsDefault, option.key, option.showIf?.allowVerificationMethodDual, option.value, options, setOptions]);

  const renderAutocomplete = (method: typeof verificationMethods[number]) => {
    const isEnabled = option.showIf[method.showIfKey];
    if (!isEnabled) return null;

    const newOptionsBasedType = (method.key === 'dualVerificationTypes' ? allOptions : idDocumentsDefault) || [];

    const handleChange = (currentMethod: MethodDocumentKey, value: OptionItem[]) => {
      const selectedKeys = value.map((item: OptionItem) => item.key);

      if (selectedKeys.length === 0) return;

      const methodToFieldMap = {
        digitalDocumentTypes: 'digitalMethodDocumentTypes',
        creditCheckDocumentTypes: 'creditCheckDocumentTypes',
        dualVerificationTypes: 'dualMethodDocumentTypes',
        inPersonVerificationTypes: 'inPersonMethodDocumentTypes',
      };

      setOptions(options.map((opt: SubStepOption) => (opt.key === option.key
        ? {
          ...opt,
          value: selectedKeys,
          default: selectedKeys,
          [methodToFieldMap[currentMethod]]: currentMethod === 'creditCheckDocumentTypes'
            ? selectedKeys[0]
            : selectedKeys,
        }
        : opt)));
    };

    return (
      <Autocomplete
        key={method.key}
        label={method.label}
        options={newOptionsBasedType}
        value={
          (Array.isArray(option.value)
            ? option.value
            : option.value ? [option.value] : []
          ).map((key: string) => {
            const optionItem = newOptionsBasedType.find(
              (item: OptionItem) => item.key === key,
            );
            return optionItem ? {
              key: optionItem.key,
              label: optionItem.label,
            } : null;
          }).filter(Boolean) as OptionItem[]
        }
        isOptionEqualToValue={(optionItem: OptionItem, value: OptionItem) => optionItem.key === value.key}
        multiple={option.options?.allowMultiple}
        fullWidth
        onChange={(e: React.SyntheticEvent, value: any) => handleChange(method.key as MethodDocumentKey, value)}
      />
    );
  };

  return (
    <Box sx={{ paddingBottom: '15px' }}>
      {verificationMethods.map((method) => renderAutocomplete(method))}
    </Box>
  );
};

export default AutocompleteFieldIdVerification;
